import type { SharedEntityMeta } from "types/chat";
import {
  buildCommentSharePath,
  buildEntitySharePath,
  buildQuizSharePath,
  buildShareRouteUrl,
} from "util/shareUrls";

const FALLBACK_ORIGIN = "https://societea.app";

const getAppOrigin = (): string => {
  if (typeof window === "undefined" || !window.location?.origin) {
    return FALLBACK_ORIGIN;
  }

  return window.location.origin.replace(/\/$/, "");
};

const normalizeEntityPath = (pathname: string): string =>
  pathname.startsWith("/") ? pathname : `/${pathname}`;

export interface ParsedSharedEntityUrl {
  entityType: string;
  entityId: string;
  canonicalPath: string;
  sharePath: string;
}

/**
 * Build the canonical in-app path for a shared entity.
 */
export const getSharedEntityCanonicalPath = (meta: SharedEntityMeta): string => {
  if (meta.canonical_path) {
    return normalizeEntityPath(meta.canonical_path);
  }

  switch (meta.entity_type) {
    case "issue":
      return buildEntitySharePath("issue", meta.entity_cname || meta.entity_id);
    case "impact":
    case "teabate":
    case "solution":
    case "action":
    case "feedback":
      return buildEntitySharePath(meta.entity_type, meta.entity_id);
    case "collection":
      return `/collection/${encodeURIComponent(String(meta.entity_id))}`;
    case "user":
      return `/user/${encodeURIComponent(String(meta.entity_id))}`;
    case "team":
      return `/partea/${encodeURIComponent(String(meta.entity_id))}`;
    case "quiz":
      return buildQuizSharePath(String(meta.entity_id));
    case "comment":
      return buildCommentSharePath(meta.entity_id);
    default:
      return "/";
  }
};

/**
 * Build the locale-neutral `/s/*` path for a shared entity.
 */
export const getSharedEntitySharePath = (meta: SharedEntityMeta): string => {
  if (meta.share_path) {
    return normalizeEntityPath(meta.share_path);
  }

  switch (meta.entity_type) {
    case "comment":
      return `/s${buildCommentSharePath(meta.entity_id)}`;
    case "quiz":
      return `/s${buildQuizSharePath(String(meta.entity_id))}`;
    case "collection":
      return `/s/collection/${encodeURIComponent(String(meta.entity_id))}`;
    case "user":
      return `/s/user/${encodeURIComponent(String(meta.entity_id))}`;
    case "team":
      return `/s/partea/${encodeURIComponent(String(meta.entity_id))}`;
    case "issue":
      return `/s${buildEntitySharePath("issue", meta.entity_cname || meta.entity_id)}`;
    default:
      return `/s${buildEntitySharePath(meta.entity_type, meta.entity_id)}`;
  }
};

/**
 * Build the absolute in-app URL for a shared entity.
 */
export const getSharedEntityCanonicalUrl = (meta: SharedEntityMeta): string =>
  `${getAppOrigin()}${getSharedEntityCanonicalPath(meta)}`;

/**
 * Build the absolute outbound share URL for a shared entity.
 */
export const getSharedEntityShareUrl = (meta: SharedEntityMeta): string =>
  buildShareRouteUrl(getSharedEntityCanonicalPath(meta));

/**
 * Return a presentable label for a shared entity type.
 */
export const getSharedEntityKindLabel = (entityType: string): string => {
  switch (entityType) {
    case "issue":
      return "Issue";
    case "impact":
      return "Impact";
    case "teabate":
      return "Teabate";
    case "solution":
      return "Solution";
    case "action":
      return "Action";
    case "feedback":
      return "Feedback";
    case "collection":
      return "Collection";
    case "comment":
      return "Comment";
    case "user":
      return "User";
    case "team":
      return "Partea";
    case "quiz":
      return "Quiz";
    default:
      return "Shared";
  }
};

/**
 * Parse a Societea content URL into a chat-share entity target.
 */
export const parseSharedEntityUrl = (urlValue: string): ParsedSharedEntityUrl | null => {
  try {
    const parsedUrl = new URL(urlValue, getAppOrigin());
    let pathname = parsedUrl.pathname;

    if (pathname.startsWith("/s/")) {
      pathname = pathname.slice(2);
    }

    const segments = pathname.split("/").filter(Boolean);
    if (segments.length < 2) {
      return null;
    }

    const [entityType, entityId] = segments;

    if (
      entityType === "issue" ||
      entityType === "impact" ||
      entityType === "teabate" ||
      entityType === "solution" ||
      entityType === "action" ||
      entityType === "feedback" ||
      entityType === "collection" ||
      entityType === "user" ||
      entityType === "quiz" ||
      entityType === "comment"
    ) {
      return {
        entityType: entityType === "collection" ? "collection" : entityType,
        entityId,
        canonicalPath: `/${entityType}/${entityId}`,
        sharePath: `/s/${entityType}/${entityId}`,
      };
    }

    if (entityType === "partea") {
      return {
        entityType: "team",
        entityId,
        canonicalPath: `/partea/${entityId}`,
        sharePath: `/s/partea/${entityId}`,
      };
    }

    return null;
  } catch {
    return null;
  }
};
